import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { LayersIcon, Trash2Icon } from 'lucide-react'
import { VERAFIED_EPISODE_STATUS_LABELS } from '@repo/types'
import { Button, Modal, Skeleton } from '@repo/ui'

import { EmptyState, Field, FormSubmitButton, TextAreaField } from 'components'
import { ConfirmSettingModal } from 'components/feedback/modals/ConfirmSettingModal/ConfirmSettingModal'
import { useSnackbar } from 'contexts'
import { useVerafiedActions, useVerafiedSegments } from 'hooks'
import type { VerafiedSegment, VerafiedSegmentInput } from 'services'

type Untyped = any
type SegmentForm = { name: string; purpose: string; typical_minutes: string; needs: string }

const blank = (v: string) => (v.trim() ? v.trim() : null)

const SegmentFormModal = ({ open, segment, onClose }: { open: boolean; segment: VerafiedSegment | null; onClose: () => void }) => {
  const { open: notify } = useSnackbar()
  const actions = useVerafiedActions()
  const methods = useForm<SegmentForm>({ mode: 'onChange' })
  const { control, reset, handleSubmit } = methods
  const [confirmDelete, setConfirmDelete] = useState(false)

  useEffect(() => {
    if (!open) return
    reset({
      name: segment?.name ?? '',
      purpose: segment?.purpose ?? '',
      typical_minutes: segment?.typical_minutes != null ? String(segment.typical_minutes) : '',
      needs: segment?.needs ?? '',
    })
  }, [open, segment, reset])

  const fail = (e: unknown) => notify({ type: 'error', text: (e as Error).message })
  const save = handleSubmit((v) => {
    const input: VerafiedSegmentInput = {
      name: v.name.trim(),
      purpose: blank(v.purpose),
      typical_minutes: v.typical_minutes.trim() ? Number(v.typical_minutes) : null,
      needs: blank(v.needs),
    }
    const done = () => {
      onClose()
    }
    if (segment) actions.updateSegment.mutate({ id: segment.id, input }, { onSuccess: done, onError: fail })
    else actions.createSegment.mutate(input, { onSuccess: done, onError: fail })
  })
  const m = methods as Untyped
  const saving = actions.createSegment.isPending || actions.updateSegment.isPending

  return (
    <>
      <Modal
        title={segment ? 'Edit segment' : 'Add segment'}
        open={open}
        onClose={onClose}
        actions={
          <>
            {segment && (
              <Button
                variant="secondary"
                destructive="hover"
                startIcon={<Trash2Icon size={18} strokeWidth={1} />}
                onClick={() => setConfirmDelete(true)}
                sx={{ mr: 'auto' }}
              >
                Delete segment
              </Button>
            )}
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <FormSubmitButton control={control} requireDirty={!!segment} onClick={() => save()} loading={saving}>
              {segment ? 'Save' : 'Add segment'}
            </FormSubmitButton>
          </>
        }
      >
        <Stack spacing={4} mt={2}>
          <Field {...m} name="name" label="Name" rules={{ required: 'Give the segment a name' }} />
          <TextAreaField {...m} name="purpose" label="What it is" minRows={2} />
          <Field
            {...m}
            name="typical_minutes"
            label="Typical length, minutes (optional)"
            placeholder="8"
            rules={{ pattern: { value: /^\d{1,3}$/, message: 'Whole minutes' } }}
          />
          <TextAreaField {...m} name="needs" label="What it needs (optional)" placeholder="Props, a guest, listener questions" minRows={2} />
        </Stack>
      </Modal>
      <ConfirmSettingModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={`Delete ${segment?.name ?? 'this segment'}?`}
        consequence={
          segment?.episodes.length
            ? `It is in the running order of ${segment.episodes.length} ${segment.episodes.length === 1 ? 'episode' : 'episodes'}, and will be taken out of each.`
            : 'No episode uses it.'
        }
        confirmLabel="Delete segment"
        loading={actions.deleteSegment.isPending}
        onConfirm={() => {
          if (!segment) return
          actions.deleteSegment.mutate(segment.id, {
            onSuccess: () => {
              setConfirmDelete(false)
              onClose()
            },
            onError: fail,
          })
        }}
      />
    </>
  )
}

const GRID_SX = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 3 }

const Detail = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Stack spacing={0.5}>
    <Typography variant="body3" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body3" sx={{ lineHeight: 1.5 }}>
      {children}
    </Typography>
  </Stack>
)

/**
 * VRG-734 — the format library: reusable segments an episode is built from, in order. Seeded with
 * Spencer's six (2026-10-22) plus The Interview (2026-10-23), migration 0169; each card shows the episodes that use it.
 */
export const FormatsView = ({ canManage, adding, onAddingDone }: { canManage: boolean; adding: boolean; onAddingDone: () => void }) => {
  const theme = useTheme()
  const { data, isLoading, error } = useVerafiedSegments()
  const [editing, setEditing] = useState<VerafiedSegment | null>(null)
  const segments = data ?? []

  if (error) return <EmptyState title="Could not load segments" text={(error as Error).message} icon={<LayersIcon strokeWidth={1} size={32} />} />

  return (
    <>
      {isLoading ? (
        <Box sx={GRID_SX}>
          {Array.from({ length: 6 }, (_, i) => (
            <Stack key={i} spacing={3} sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: 'primary.100' }}>
              <Skeleton height={18} width="50%" />
              <Skeleton lines={2} />
              <Skeleton height={14} width="70%" />
            </Stack>
          ))}
        </Box>
      ) : segments.length === 0 ? (
        <EmptyState title="No segments yet" text="Add the first segment of the format library." icon={<LayersIcon strokeWidth={1} size={32} />} />
      ) : (
        <Box sx={GRID_SX}>
          {segments.map((s) => (
            <Box
              key={s.id}
              role={canManage ? 'button' : undefined}
              tabIndex={canManage ? 0 : undefined}
              onClick={canManage ? () => setEditing(s) : undefined}
              onKeyDown={(e) => {
                if (canManage && e.key === 'Enter') setEditing(s)
              }}
              data-testid={`segment-card-${s.slug}`}
              sx={{
                p: 4,
                borderRadius: 3,
                border: `1px solid ${theme.palette.primary[100]}`,
                backgroundColor: 'background.paper',
                cursor: canManage ? 'pointer' : 'default',
                '&:hover, &:focus-visible': canManage
                  ? { borderColor: theme.palette.brand[600], boxShadow: `0 0 0 3px ${theme.palette.brand['600-6%']}`, outline: 'none' }
                  : {},
              }}
            >
              <Stack spacing={3}>
                <Stack direction="row" spacing={2} alignItems="baseline" justifyContent="space-between">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {s.name}
                  </Typography>
                  {s.typical_minutes != null && (
                    <Typography variant="body3" color="text.secondary" sx={{ flexShrink: 0 }}>
                      ~{s.typical_minutes} min
                    </Typography>
                  )}
                </Stack>
                {s.purpose && (
                  <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    {s.purpose}
                  </Typography>
                )}
                <Detail label="Needs">{s.needs || 'Nothing extra'}</Detail>
                <Detail label="Used in">
                  {s.episodes.length
                    ? s.episodes.map((e) => `${e.title} (${VERAFIED_EPISODE_STATUS_LABELS[e.status].toLowerCase()})`).join('; ')
                    : 'No episode yet'}
                </Detail>
              </Stack>
            </Box>
          ))}
        </Box>
      )}
      <SegmentFormModal
        open={adding || !!editing}
        segment={editing}
        onClose={() => {
          setEditing(null)
          onAddingDone()
        }}
      />
    </>
  )
}
